import { useEffect, useState } from 'react'
import { cn } from '../../lib/utils'

interface ContactInfo {
  name?: string
  email?: string
  phone?: string
}

interface FooterProps {
  className?: string
}

export function Footer({ className }: FooterProps) {
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [version, setVersion] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()

    const fetchContactInfo = async () => {
      try {
        const response = await fetch('/api/configuration/contact', { signal: controller.signal })
        if (response.ok) {
          const data = await response.json()
          setContactInfo(data)
        }
      } catch (error) {
        if (controller.signal.aborted) return
        console.error('Failed to fetch contact info:', error)
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      }
    }

    const fetchVersion = async () => {
      try {
        const response = await fetch('/api/version', { signal: controller.signal })
        if (response.ok) {
          const data = await response.json()
          if (data?.version) setVersion(data.version)
        }
      } catch (error) {
        if (controller.signal.aborted) return
        console.error('Failed to fetch version:', error)
      }
    }

    fetchContactInfo()
    fetchVersion()

    return () => controller.abort()
  }, [])

  const currentYear = new Date().getFullYear()

  return (
    <footer className={cn(
      'h-[40px] bg-background border-t border-border mt-auto',
      className
    )}>
      <div className="h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-full text-sm">
          {/* Contact Information */}
          <div className="text-text-muted italic">
            {loading ? (
              <span>Loading contact info...</span>
            ) : contactInfo ? (
              <span>
                If you have any issues using this site, please contact RSUI at:{' '}
                {contactInfo.email && (
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="text-primary hover:underline"
                  >
                    {contactInfo.email}
                  </a>
                )}
                {contactInfo.email && contactInfo.phone && ' or '}
                {contactInfo.phone && (
                  <a
                    href={`tel:${contactInfo.phone.replace(/\D/g, '')}`}
                    className="text-primary hover:underline"
                  >
                    {contactInfo.phone}
                  </a>
                )}
              </span>
            ) : (
              <span>
                If you have any issues using this site, please contact RSUI support.
              </span>
            )}
          </div>

          {/* Copyright */}
          <div
            className="text-text-muted text-xs cursor-default"
            title={version ? `Version ${version}` : undefined}
          >
            &copy; {currentYear} RSUI
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
